import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldWarning, Clock, EnvelopeSimple, Phone, ArrowsClockwise } from "@phosphor-icons/react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminAstrologerApplicationsPage() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/astrologer-applications");
      setItems(data.applications || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  if (authLoading) {
    return (
      <PageShell><Header />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-center text-muted">Loading…</div>
        <Footer />
      </PageShell>
    );
  }

  if (!user || !user.is_super_admin) {
    return (
      <PageShell><Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="admin-apps-denied">
          <ShieldWarning size={36} weight="duotone" className="mx-auto text-rose-600" />
          <h1 className="font-serif-display text-3xl mt-4 font-light text-[#3F2D1B]">Super-admin only</h1>
          <p className="text-muted mt-3 font-light max-w-md mx-auto leading-relaxed">
            This page lists astrologer registration applications and is restricted to AstroKuber super-admins.
          </p>
          <Link to="/" className="inline-block mt-7 px-6 py-2.5 rounded-full bg-[#3F2D1B] text-amber-50 text-sm font-bold tracking-wider">
            Back to home
          </Link>
        </div>
        <Footer />
      </PageShell>
    );
  }

  return (
    <PageShell><Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-apps-page">
        <header className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-[#7C2D12]">Super-admin</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 font-light leading-tight text-[#3F2D1B]">
              Astrologer applications <span className="text-muted text-base font-light">· {items.length}</span>
            </h1>
            <p className="text-muted mt-3 font-light text-sm max-w-2xl">
              Every person who submits the "Become an astrologer" form lands here. Review, then email them at the address listed to begin onboarding.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-apps-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300 text-[#3F2D1B] text-xs font-bold tracking-wider hover:bg-amber-50"
          >
            <ArrowsClockwise size={14} weight="bold" /> REFRESH
          </button>
        </header>

        {error && (
          <div className="mt-8 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="admin-apps-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="mt-12 text-center text-muted">Loading applications…</div>
        ) : items.length === 0 ? (
          <div className="mt-16 rounded-2xl p-12 bg-amber-50 border border-amber-200 text-center" data-testid="admin-apps-empty">
            <p className="font-serif-display text-2xl font-light text-[#3F2D1B]">No applications yet.</p>
            <p className="text-muted mt-2 text-sm font-light">When seekers submit the registration form, they'll show up here.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-5">
            {items.map((a) => (
              <article key={a.application_id} className="rounded-2xl overflow-hidden border border-amber-200 bg-white" data-testid={`appl-${a.application_id}`}>
                <header className="p-5 sm:p-6 bg-gradient-to-br from-[#FFF8E7] to-[#FFE7C2] border-b border-amber-200">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-300 text-amber-950">{(a.status || "PENDING").toUpperCase()}</span>
                    <h2 className="font-serif-display text-2xl font-light text-[#3F2D1B]">{a.name}</h2>
                    <span className="text-xs text-muted flex items-center gap-1"><Clock size={12} weight="duotone" /> {fmtDateTime(a.submitted_at)}</span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-[#3F2D1B]">
                    <span className="flex items-center gap-1.5"><EnvelopeSimple size={12} weight="duotone" />
                      <a href={`mailto:${a.email}`} className="underline">{a.email}</a>
                    </span>
                    {a.phone && (
                      <span className="flex items-center gap-1.5"><Phone size={12} weight="duotone" /> {a.phone}</span>
                    )}
                    <span><b>Experience:</b> {a.experience_years} yr{a.experience_years === 1 ? "" : "s"}</span>
                    <span><b>Rate:</b> ₹{a.rate_per_min} / min</span>
                  </div>
                </header>
                <div className="p-5 sm:p-6 grid sm:grid-cols-2 gap-5 text-sm">
                  <div>
                    <div className="label-eyebrow text-muted">Specializations</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(a.specializations || []).map((s) => (
                        <span key={s} className="px-2.5 py-1 rounded-full bg-violet-100 text-violet-900 text-[11px] font-medium">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="label-eyebrow text-muted">Languages</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(a.languages || []).map((l) => (
                        <span key={l} className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-900 text-[11px] font-medium">{l}</span>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="label-eyebrow text-muted">Bio</div>
                    <p className="mt-2 text-[#3F2D1B] font-light leading-relaxed whitespace-pre-wrap">{a.bio}</p>
                  </div>
                  <div className="sm:col-span-2 text-[11px] text-muted font-light pt-2 border-t border-soft">
                    Submitted by signed-in account: <b>{a.applicant_email_login || "—"}</b> · Application ID: <code className="font-mono">{a.application_id}</code>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </PageShell>
  );
}
